import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { announce } from '@theme/live-status';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /**
   * ONE DEBOUNCE PER CART LINE, NOT ONE FOR THE CART.
   *
   * The stock component shared a single 300ms debounce across every stepper. A debounce keeps only
   * its last call, so pressing + on one line and then + on another inside 300ms sent the second
   * change and silently discarded the first. The discarded line kept the number the stepper had
   * already drawn, while the total, the line price, the badge and the case slots all stayed on the
   * cart the server actually held: the drawer disagreed with itself and nothing reported an error.
   * Measured on theme 208048619850, 30 September 2026: two presses 150ms apart, one
   * /cart/change request.
   *
   * Keyed by line so repeated presses on the SAME line still collapse into one request, which is
   * what the debounce was for.
   *
   * @type {Map<number, ((event: QuantitySelectorUpdateEvent) => void) & { cancel(): void }>}
   */
  #debouncedByLine = new Map();

  /** @param {Event} event */
  #debouncedOnChange = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent)) return;

    // Cart items require a line number; selectors outside the cart carry none.
    const line = event.detail.cartLine;
    if (!line) return;

    let debounced = this.#debouncedByLine.get(line);
    if (!debounced) {
      debounced = debounce(
        /** @param {QuantitySelectorUpdateEvent} lineEvent */
        (lineEvent) => this.#onQuantityChange(lineEvent),
        300
      );
      this.#debouncedByLine.set(line, debounced);
    }
    debounced(event);
  };

  /**
   * Cart changes go out one at a time. With a debounce per line two changes can now be due within
   * milliseconds of each other, and each response carries the whole section: answered out of
   * order, the older cart would be morphed in last and the drawer would be stale again.
   *
   * @type {Promise<void>}
   */
  #cartChangeQueue = Promise.resolve();
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.addEventListener('submit', this.#handleRailSubmit);
    this.addEventListener('click', this.#handleHeldCheckout);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.removeEventListener('submit', this.#handleRailSubmit);
    this.removeEventListener('click', this.#handleHeldCheckout);
  }

  /**
   * "+ Add" on the suggestions rail (snippets/cart-rail.liquid). Without JavaScript the form posts
   * to /cart/add and lands on /cart; with it the wine is added in place and the cart re-renders
   * through #handleCartUpdate, which tags the new line and shows the toast.
   *
   * A REFUSAL IS NOT A NETWORK FAILURE. When Shopify answers with a `status` (422: not enough stock,
   * a product sold only on a plan), the same add will be refused again, so the "Try again" banner
   * would only repeat it. Shopify's own words go in a toast, announced, and nothing offers a retry.
   * The banner is for a request that never got an answer. gate:cart-rail-refusal holds both paths.
   *
   * A 422 CAN STILL HAVE ADDED SOME. Measured on the preview, 2026-10-05: asking for 9,999 of a wine
   * answered 422 "Only 15 items were added to your cart due to availability." and the cart held 15
   * more. So, as the product form does, the cart is read back and the event resolves with it,
   * flagged `didError`: the drawer and the count show what the cart holds, and nothing treats it as
   * a clean add (the quick add dialog stays open, the drawer does not auto-open).
   * @param {SubmitEvent} event
   */
  #handleRailSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-cart-rail-add')) return;
    event.preventDefault();
    const button = form.querySelector('button');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('is-busy');

    const formData = new FormData(form);
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: this.isDrawer ? 'add' : 'update',
        context: 'cart',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: Number(formData.get('quantity')) || 1 }],
        promise: deferred.promise,
      })
    );

    const cfg = fetchConfig('javascript', { body: formData });
    fetch(Theme.routes.cart_add_url, { ...cfg, headers: { ...cfg.headers, Accept: 'application/json' } })
      .then((response) => response.json())
      .then(async (response) => {
        const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );
        if (response.status) {
          this.#toast(response.description || response.message || this.#alertText(), 'error');
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, itemCount: cart.item_count, source: 'cart-rail', didError: true },
          });
          return;
        }
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { sections: response.sections, items: cart.items, itemCount: cart.item_count, source: 'cart-rail', didError: false },
        });
      })
      .catch((error) => {
        deferred.reject(error);
        this.#showNetworkError(() => form.requestSubmit(), button instanceof HTMLElement ? button : null);
      })
      .finally(() => {
        button?.removeAttribute('aria-busy');
        button?.classList.remove('is-busy');
      });
  };

  /**
   * Check out held back while a sold-out line is in the cart (state I). The button carries
   * aria-disabled rather than disabled, so it stays focusable and its reason stays announced.
   * @param {MouseEvent} event
   */
  #handleHeldCheckout = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-checkout-held]') : null;
    if (target) event.preventDefault();
  };

  /**
   * The banner at the top of the list when a change could not be saved (state H): offline, or the
   * server refused. The cart on screen is the last saved one. Try again repeats the change.
   * Inserted empty and filled a frame later, so its role="alert" announces (gate:live-region-birth
   * holds the same rule for the theme's markup).
   * Try again removes its own banner, so focus goes first to the control the change came from
   * (the line's quantity field); left on the button, it fell to <body> (measured at 390 and 1440
   * on 208733503818, 2026-10-01).
   * @param {() => void} retry
   * @param {HTMLElement | null | undefined} [focusAfter] where focus goes when Try again is pressed
   */
  #showNetworkError(retry, focusAfter) {
    const template = this.querySelector('template[data-cart-alert]');
    const wrapper = this.querySelector('.cart-items__wrapper');
    if (!(template instanceof HTMLTemplateElement) || !wrapper) return;
    this.querySelector('.gv-alert')?.remove();
    const alert = document.createElement('div');
    alert.className = 'gv-alert';
    alert.setAttribute('role', 'alert');
    wrapper.prepend(alert);
    requestAnimationFrame(() => {
      alert.append(document.importNode(template.content, true));
      alert.querySelector('[data-cart-alert-retry]')?.addEventListener('click', () => {
        // A frame later, not inside the click: a target detached mid-click reads as a click on
        // the drawer's backdrop and closes it (the same trap Undo hit, see undoLineItemRemove).
        if (focusAfter?.isConnected && alert.contains(document.activeElement)) {
          focusAfter.focus({ preventScroll: true });
        }
        requestAnimationFrame(() => alert.remove());
        retry();
      });
    });
  }

  /** The network banner's own sentence, for a refusal that came back with no words of its own. */
  #alertText() {
    const template = this.querySelector('template[data-cart-alert]');
    const text = template instanceof HTMLTemplateElement ? template.content.querySelector('span:not([aria-hidden])')?.textContent : '';
    return text?.trim() || 'Add to cart failed';
  }

  /**
   * A toast in the drawer (states A and B): visual, with what it says announced once through the
   * persistent live region. Fades after five seconds, and waits while hovered or focused.
   * An error toast carries the banner's alert icon instead of the tick, so a refusal never reads
   * as a confirmation.
   * @param {string} text
   * @param {'done' | 'error'} [tone]
   */
  #toast(text, tone = 'done') {
    const host = this.closest('dialog')?.querySelector('[data-cart-toasts]');
    const template = host?.querySelector('template');
    announce(text);
    if (!(host instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;
    const toast = /** @type {HTMLElement | null} */ (document.importNode(template.content, true).firstElementChild);
    if (!toast) return;
    const slot = toast.querySelector('[data-toast-text]');
    if (slot) slot.textContent = text;
    if (tone === 'error') {
      toast.dataset.tone = 'error';
      const alertTemplate = this.querySelector('template[data-cart-alert]');
      const alertIcon = alertTemplate instanceof HTMLTemplateElement ? alertTemplate.content.querySelector('[aria-hidden="true"]') : null;
      const tick = toast.querySelector('[aria-hidden="true"]');
      if (alertIcon && tick) tick.replaceWith(document.importNode(alertIcon, true));
    }
    host.querySelectorAll('.gv-toast').forEach((old) => old.remove());
    host.append(toast);
    let timer = 0;
    const leave = () => {
      toast.setAttribute('data-leaving', '');
      window.setTimeout(() => toast.remove(), prefersReducedMotion() ? 0 : 250);
    };
    const arm = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(leave, 5000);
    };
    toast.addEventListener('mouseenter', () => window.clearTimeout(timer));
    toast.addEventListener('mouseleave', arm);
    toast.addEventListener('focusin', () => window.clearTimeout(timer));
    toast.addEventListener('focusout', arm);
    toast.querySelector('.gv-toast__close')?.addEventListener('click', leave);
    arm();
  }

  /**
   * Where focus is, by the line and the control, so it can be put back if the morph re-renders
   * that control away (a stepper press that crosses a case tier re-renders more of the drawer and
   * dropped focus to <body>, measured at 390 on 208733503818, 2026-10-01).
   *
   * THE LINE'S KEY IS NOT STABLE ACROSS THAT CHANGE: when the press brings an automatic discount in
   * (the store's "6 Bottles"), Shopify gives the line a new key, so a lookup by the old key finds
   * nothing (measured, same preview: focus stayed on <body>). The line is found again by the
   * variant at the same position, then by the variant anywhere.
   * @returns {() => void} Restores focus to the same control, only if focus was lost.
   */
  #focusAnchor() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return () => {};
    const row = active.closest('[data-key]');
    const key = row?.getAttribute('data-key') ?? '';
    const variant = key.split(':')[0];
    const index = row ? [...this.querySelectorAll('[data-key]')].indexOf(row) : -1;
    const role = ['.quantity-plus', '.quantity-minus', 'input[type="number"]', '.cart-items__remove'].find((sel) =>
      active.matches(sel)
    );
    return () => {
      const now = document.activeElement;
      if (now && now !== document.body && now.isConnected) return;
      if (!key || !role) return;
      const rows = [...this.querySelectorAll('[data-key]')];
      const same = (/** @type {Element} */ r) => (r.getAttribute('data-key') ?? '').split(':')[0] === variant;
      const line =
        rows.find((r) => r.getAttribute('data-key') === key) ??
        (rows[index] && same(rows[index]) ? rows[index] : undefined) ??
        rows.find(same);
      const target = line?.querySelector(role);
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    };
  }

  /** @returns {{ count: number, tier: number }} the case counter's state as rendered */
  #caseState() {
    const bar = this.querySelector('.gv-casebar');
    return {
      count: Number(bar?.getAttribute('data-case-count') ?? 0),
      tier: Number(bar?.getAttribute('data-case-tier') ?? 0),
    };
  }

  /**
   * After a morph: a toast once per crossing of a case tier, upwards only (state B).
   * @param {{ count: number, tier: number }} before
   */
  #announceMilestone(before) {
    if (!this.isDrawer) return;
    const after = this.#caseState();
    if (after.tier <= before.tier) return;
    const host = this.closest('dialog')?.querySelector('[data-cart-toasts]');
    const key = after.tier >= 2 ? 'milestoneTwo' : 'milestoneOne';
    const text = host instanceof HTMLElement ? host.dataset[key] : '';
    if (text) this.#toast(text);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // The undo lasts until the cart next changes, which is what makes it need no timer. Editing a
    // quantity is such a change: the cart the undo was captured against no longer exists, and an
    // undo offering to restore a line into a cart that has moved on is a promise about a state the
    // customer has left. Removing another line replaces the undo rather than clearing it, which is
    // handled where that capture happens.
    this.#pendingUndo = null;
    this.#dismissUndo();

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows?.[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];

    // The undo snapshot is read BEFORE the change request is sent. Once the removal lands the line
    // is gone from /cart.js, taking its properties and selling plan with it, so reading it alongside
    // the POST lost whenever the POST won. The exit animation below still starts at once, so the
    // customer sees no delay; only the POST waits for one read. One pending undo at a time.
    const captured = cartItemRowToRemove ? this.#captureForUndo(cartItemRowToRemove) : Promise.resolve();
    captured.finally(() => {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    /**
     * WHERE FOCUS GOES, DECIDED BEFORE ANYTHING IS DESTROYED.
     *
     * The button that was just pressed is inside the row being removed, so the press destroys its
     * own focus and the browser drops it to `<body>`. A keyboard or screen-reader user is returned
     * to the top of the document and has to walk the whole cart again to remove a second line.
     *
     * This is the one case in this component that `morph()` cannot cover. Elsewhere it preserves
     * focus deliberately (see assets/morph.js: it keeps "custom elements, listeners, focus,
     * transient UI state" by morphing rather than replacing), which is why a stepper press keeps
     * its focus and needs nothing here. Morph can only preserve a node that still exists, and this
     * path is the one that removes it.
     *
     * The successor is computed now rather than after, because afterwards the row is gone and its
     * position with it. Next row if there is one, otherwise the previous: the same direction of
     * travel the list already had, so focus does not jump backwards while the list shortens ahead
     * of it.
     */
    const focusTarget = this.#successorFocusTarget(rowsToRemove);

    // Where the undo row goes: the row after the removed set, read now while it is still in the
    // list (see #offerUndo).
    const lastRemoved = rowsToRemove[rowsToRemove.length - 1];
    const undoAnchor = lastRemoved?.nextElementSibling ?? null;
    this.#undoAnchorKey = undoAnchor instanceof HTMLElement ? undoAnchor.dataset.key ?? null : null;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        // The drawer keeps its "Your cart" header (state M); only its bottle count goes.
        const count = this.closest('dialog')?.querySelector('.cart-drawer__count');
        if (count) count.textContent = '';
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      // THE LAST LINE GETS AN UNDO TOO (state M): the row goes above the empty state and takes
      // focus, so a slip is one key to reverse. It waits for the snapshot; until then, and if the
      // snapshot fails, focus goes to the empty content, which is what replaced the list.
      requestAnimationFrame(() => this.#focusEmptyCart());
      captured.then(() => {
        const undoButton = this.#offerUndo(null, null);
        if (undoButton) undoButton.focus();
      });

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    // After the rows are gone, not before: focusing an element inside a row that is still
    // animating out would move focus onto something about to be removed.
    //
    // The undo row is offered into the space the removed line vacated, and focus prefers its Undo
    // control over the successor remove button. One mechanism rather than two: the control that
    // recovers from the misclick is the control focus lands on, so a keyboard user who removed the
    // wrong line presses Enter rather than hunting for a way back.
    const restore = () => {
      const undoButton = this.#offerUndo(cartItemRowToRemove, undoAnchor);
      const target = undoButton ?? focusTarget;
      if (target?.isConnected) target.focus();
      // No undo yet (the snapshot was still in flight): remember that focus went to the successor
      // as a stand-in, so the undo can take it when it arrives (#reofferUndoAfterMorph).
      if (!undoButton && target === focusTarget) {
        this.#fallbackFocusKey = focusTarget?.closest('[data-key]')?.getAttribute('data-key') ?? null;
      }
    };

    if (prefersReducedMotion()) restore();
    else onAnimationEnd(cartItemRowToRemove, restore);
  }

  /**
   * The `data-key` of the row the cart page's undo row sits in front of, so the row can be put
   * back in the same place after a morph has rebuilt the list. Null means the end of the list.
   * @type {string | null}
   */
  #undoAnchorKey = null;

  /** Whether the pending undo has been announced yet. One announcement per removal. */
  #undoAnnounced = false;

  /**
   * The row whose Remove took focus as a stand-in because the undo was not ready. When the undo
   * arrives and focus is still there, it moves to Undo.
   * @type {string | null}
   */
  #fallbackFocusKey = null;

  /**
   * THE CART PAGE'S UNDO SURVIVES THE MORPH. The page re-renders in `full` mode, which rebuilds
   * the list from the server's HTML, and the server has never heard of the undo row. Measured on
   * the development theme, 2026-09-30: the row appeared with focus on Undo, and 500ms later the
   * removal's response morphed the list and took the row with it, dropping focus to <body>. So
   * after every page morph, a pending undo is offered again in its place, without a second
   * announcement, and focus goes back to it if the morph had thrown it to the body. It also
   * covers the order the other way round: a response that lands before the exit animation ends
   * removes the animating row, its animationend never fires, and without this the undo was
   * never offered at all, and the reduced-motion path, where the row is removed at once and the
   * snapshot usually has not landed yet. Whichever path inserts the row FIRST announces it
   * (#undoAnnounced); focus moves to it if the morph threw focus to the body, or if it is still on
   * the stand-in Remove that took it while the undo was not ready.
   *
   * THE DRAWER NEEDS IT TOO. It was left out on the reading that the drawer's `hydration` morph
   * leaves the list alone. It does not: `.cart-drawer__inner` carries the hydration key, so the
   * whole list inside it is morphed from the server's HTML. Measured on 208733503818, 2026-10-01,
   * at 390 and 1440: the drawer's undo row appeared with focus on Undo and was gone about 400ms
   * later, focus on <body>, so a drawer shopper never had a usable Undo.
   */
  #reofferUndoAfterMorph() {
    if (!this.#pendingUndo) return;
    if (this.querySelector('.cart-items__undo-row')) return;
    if (!this.querySelector('tbody, [data-undo-host]')) return;
    const body = this.querySelector('tbody');
    const anchor = this.#undoAnchorKey
      ? body?.querySelector(`:scope > [data-key="${CSS.escape(this.#undoAnchorKey)}"]`) ?? null
      : null;
    const active = document.activeElement;
    const lost = !active || active === document.body;
    const onStandIn =
      !!this.#fallbackFocusKey &&
      active instanceof HTMLElement &&
      active.matches('.cart-items__remove') &&
      active.closest('[data-key]')?.getAttribute('data-key') === this.#fallbackFocusKey;
    const button = this.#offerUndo(null, anchor);
    if (button && (lost || onStandIn)) {
      button.focus();
      this.#fallbackFocusKey = null;
    }
  }

  /**
   * The line a pending undo would restore, or null when there is nothing to undo.
   * @type {{ id: number, quantity: number, properties: object, selling_plan: number|null, title: string } | null}
   */
  #pendingUndo = null;

  /**
   * Reads the line about to be removed out of the cart so it can be put back.
   *
   * WHY A FETCH RATHER THAN THE DOM. The row carries `data-key` and nothing else. A line's identity
   * for re-adding is its variant id, quantity, line properties and selling plan, none of which are
   * in the markup, and two lines of the same variant differ ONLY by their properties. Reading
   * /cart.js is the one place all four are available together and correct.
   *
   * Settles BEFORE the removal is sent (see onLineItemRemove), because once the removal lands the
   * line is no longer in /cart.js. The exit animation is not held up by it; only the POST is.
   *
   * @param {HTMLElement} row - The row being removed.
   * @returns {Promise<void>} Settles once the snapshot is taken or has failed.
   */
  #captureForUndo(row) {
    const key = row.dataset.key;
    // Kept now: when the last line goes, the list (and the template inside it) is swapped for the
    // empty state before the snapshot lands, and the Undo row is built from this copy.
    const template = document.getElementById('cart-undo-template');
    if (template instanceof HTMLTemplateElement) this.#undoTemplate = template;
    this.#pendingUndo = null;
    this.#undoAnnounced = false;
    this.#fallbackFocusKey = null;
    if (!key) return Promise.resolve();

    return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const line = cart.items?.find((item) => item.key === key);
        if (!line) return;
        this.#pendingUndo = {
          id: line.variant_id ?? line.id,
          quantity: line.quantity,
          properties: line.properties ?? {},
          // The wine club writes selling_plan plus three property names onto live subscription
          // contracts (see sections/wine-club-cases.liquid). Undo copies them back verbatim rather
          // than reconstructing them; a re-add that drops one silently orphans the contract.
          selling_plan: line.selling_plan_allocation?.selling_plan?.id ?? null,
          title: line.product_title ?? line.title ?? '',
        };
      })
      .catch(() => {
        // No undo rather than a broken one. The removal itself already succeeded.
        this.#pendingUndo = null;
      });
  }

  /** @type {HTMLTemplateElement | null} the undo template, kept from the last removal */
  #undoTemplate = null;

  /**
   * Puts the undo row where the removed line was, and returns its button so focus can land on it.
   *
   * Returns null when the capture has not resolved or the template is absent, in which case focus
   * falls back to the successor remove button and the customer simply gets no undo. A missing undo
   * is a smaller failure than a focus trap.
   *
   * THE ROW TAKES THE REMOVED LINE'S PLACE, in the drawer and on the page: it is inserted before
   * the row that followed the removed one, so the recovery sits where the eye already is. With no
   * list left (the last line went) it goes above the empty state instead (side cart state M).
   *
   * @param {HTMLElement | null} removedRow - The row that was removed (unused; kept for callers).
   * @param {Element | null} [anchor] - The row that followed it, read before it was removed.
   * @returns {HTMLElement | null} The Undo button, if one was inserted.
   */
  #offerUndo(removedRow, anchor = null) {
    const live = document.getElementById('cart-undo-template');
    const template = live instanceof HTMLTemplateElement ? live : this.#undoTemplate;
    if (!this.#pendingUndo || !(template instanceof HTMLTemplateElement)) return null;

    this.#dismissUndo();

    const clone = document.importNode(template.content, true);
    let undoRow = clone.querySelector('.cart-items__undo-row');
    if (!(undoRow instanceof HTMLElement)) return null;

    const message = undoRow.querySelector('.cart-items__undo-message');
    if (message instanceof HTMLElement) {
      const token = message.dataset.titleToken ?? '__TITLE__';
      message.textContent = (message.textContent ?? '').replace(token, this.#pendingUndo.title);
    }

    // The page's Undo names the wine in its accessible name ("Undo removing ..."), through the
    // same token, so the sentence stays in the locale files.
    const undoButtonLabel = undoRow.querySelector('.cart-items__undo-button[aria-label]');
    if (undoButtonLabel instanceof HTMLElement) {
      const token = undoButtonLabel.dataset.titleToken ?? '__TITLE__';
      undoButtonLabel.setAttribute(
        'aria-label',
        (undoButtonLabel.getAttribute('aria-label') ?? '').replace(token, this.#pendingUndo.title)
      );
    }

    const body = this.querySelector('tbody');
    const host = body ? null : this.querySelector('[data-undo-host]');
    if (!body && !host) return null;
    if (body) {
      // A morph may have rebuilt the rows since the anchor was read: find it again by its key.
      if (anchor && !anchor.isConnected && this.#undoAnchorKey) {
        anchor = body.querySelector(`:scope > [data-key="${CSS.escape(this.#undoAnchorKey)}"]`);
      }
      // In place in both surfaces: the recovery sits where the eye already is.
      if (anchor && anchor.parentNode === body) body.insertBefore(undoRow, anchor);
      else body.appendChild(undoRow);
    } else if (host) {
      // No list left (the last line went): the same row, as a block above the empty state. A <tr>
      // outside a table is not valid, so its cell's contents move into a <div> with its classes.
      const block = document.createElement('div');
      block.className = 'cart-items__undo-row';
      const cell = document.createElement('div');
      cell.className = 'cart-items__undo';
      const td = undoRow.querySelector('.cart-items__undo');
      if (td) cell.append(...td.childNodes);
      block.append(cell);
      host.replaceChildren(block);
      undoRow = block;
    }

    // The row is cloned with its sentence already in it, so a live region ON the row would enter
    // the page full and never be read out. The persistent region in layout/theme.liquid is the one
    // that announces; the row keeps the sentence as visible text.
    // Once per removal, by whichever path puts the row up first.
    if (!this.#undoAnnounced && message instanceof HTMLElement) {
      announce(message.textContent?.trim() ?? '');
      this.#undoAnnounced = true;
    }

    const button = undoRow.querySelector('.cart-items__undo-button');
    return button instanceof HTMLElement ? button : null;
  }

  /**
   * Inline notes on a line that must survive the morph which follows the change they explain
   * (state G: "Only 2 left, so we've set it to 2."). Keyed by the line's key; a later change to
   * the same line clears its note.
   * @type {Map<string, string>}
   */
  #lineNotes = new Map();

  /**
   * @param {string} key
   * @param {string} text
   */
  setLineNote(key, text) {
    this.#lineNotes.set(key, text);
    this.#reapplyLineNotes();
  }

  /** @param {string} key */
  clearLineNote(key) {
    this.#lineNotes.delete(key);
  }

  #reapplyLineNotes() {
    for (const [key, text] of this.#lineNotes) {
      const row = this.querySelector(`[data-key="${CSS.escape(key)}"]`);
      const cell = row?.querySelector('.cart-items__error');
      const slot = cell?.querySelector('.cart-item__error-text');
      if (!cell || !slot) {
        this.#lineNotes.delete(key);
        continue;
      }
      cell.classList.remove('hidden');
      if (slot.textContent !== text) slot.textContent = text;
    }
  }

  /** Removes any undo row on screen. */
  #dismissUndo() {
    this.querySelectorAll('.cart-items__undo-row').forEach((row) => row.remove());
  }

  /**
   * Puts the captured line back.
   *
   * APPENDS RATHER THAN RESTORING POSITION. The Cart AJAX API has no reorder, so honouring the
   * original position means removing and re-adding every line beneath it. On this store that would
   * tear down and rebuild subscription lines nobody touched, and sections/wine-club-cases.liquid
   * records that subscriptionContracts is denied to our tooling, so the blast radius of getting one
   * wrong cannot be measured from here. A line in the wrong position is a cosmetic cost; a broken
   * contract is not.
   */
  undoLineItemRemove() {
    const line = this.#pendingUndo;
    if (!line) return;

    this.#pendingUndo = null;
    // After the click has finished bubbling, not during it. Removed synchronously, the Undo button
    // is detached by the time the click reaches the drawer's backdrop handler, which then counts
    // it as a click outside the panel (isClickedOutside: !panel.contains(target)) and closes the
    // drawer: measured on the preview at 390 and 1440, the line came back and the drawer shut.
    requestAnimationFrame(() => this.#dismissUndo());

    const body = { items: [{ id: line.id, quantity: line.quantity, properties: line.properties }] };
    if (line.selling_plan) body.items[0].selling_plan = line.selling_plan;

    // QUEUED BEHIND THE REMOVAL. Undo is focused the moment it appears, so Enter can land while
    // the removal's /cart/change is still in flight. Sent straight away, the add could merge into
    // the line that was about to be cleared (and then be cleared with it), or the removal's morph
    // could land after the undo's render and put the stale cart back on screen. On the same queue,
    // the add goes only after the removal's response has been applied.
    //
    // 'json', not 'javascript': fetchConfig drops Content-Type for 'javascript', so this JSON body
    // went out as text/plain and /cart/add answered 400 on every Undo, in the drawer and on the
    // page (measured on the preview, 2026-09-30: "Failed to undo", the line never came back).
    const mode = this.isDrawer ? 'hydration' : 'full';
    this.#cartChangeQueue = this.#cartChangeQueue
      .catch(() => {})
      .then(() => fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body: JSON.stringify(body) })))
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        // A 422 (sold out, plan withdrawn) resolves rather than rejects, so it is checked here or the
        // line is lost with nothing said. The catch below turns it into a CartErrorEvent.
        if (!response.ok) throw new Error(data.description || data.message || 'Failed to undo');
        return data;
      })
      .then(async (added) => {
        // Re-rendered in the surface's own mode: the drawer hydrates, as its other updates do.
        await sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
        // Focus goes to the line that came back. Undo removes its own button, so without this a
        // keyboard user was left on <body> after every Undo: measured on 208733503818, 2026-10-01,
        // in the drawer at 390 and 1440. Only when focus has actually been lost, never stolen.
        const active = document.activeElement;
        if (!active || active === document.body || !active.isConnected) {
          const key = added.items?.[0]?.key;
          const restored = key
            ? this.querySelector(`[data-key="${CSS.escape(key)}"] .cart-items__remove`)
            : null;
          const target = restored ?? this.querySelector('.cart-items__remove');
          if (target instanceof HTMLElement) target.focus();
        }
        const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(
          (response) => response.json()
        );
        // Announced like any other cart change, so the header count and the other cart surface
        // (drawer or page) follow; #handleCartUpdate skips events this element dispatched itself.
        // 'update', not 'add': 'add' auto-opens the drawer over the cart page.
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'update',
            context: 'cart',
            lines: [{ id: added.items?.[0]?.key ?? '', quantity: line.quantity }],
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { items: cart.items, itemCount: cart.item_count, source: 'cart-items-component', didError: false },
            }),
          })
        );
      })
      .catch((error) => {
        console.error(error);
        this.dispatchEvent(
          new CartErrorEvent({ error: error?.message || 'Failed to undo', code: 'SERVICE_UNAVAILABLE' })
        );
      });
  }

  /**
   * The control focus should land on once the given rows are removed.
   *
   * Returns the remove button of the nearest row that is NOT being removed, preferring the one
   * after the removed set. Nested lines are part of the same removal, which is why the whole set is
   * excluded rather than just the row that was pressed.
   *
   * @param {HTMLElement[]} rowsToRemove - Every row about to be removed.
   * @returns {HTMLElement | null} The button to focus, or null if none survives.
   */
  #successorFocusTarget(rowsToRemove) {
    const rows = this.refs.cartItemRows ?? [];
    const removing = new Set(rowsToRemove);
    const survivors = rows.filter((row) => !removing.has(row));
    if (survivors.length === 0) return null;

    const firstRemovedIndex = rows.indexOf(rowsToRemove[0]);
    const after = survivors.find((row) => rows.indexOf(row) > firstRemovedIndex);
    const target = after ?? survivors[survivors.length - 1];

    const button = target.querySelector('.cart-items__remove:not([hidden])');
    return button instanceof HTMLElement ? button : null;
  }

  /**
   * Moves focus to the empty-cart content after the last line is removed.
   *
   * `tabindex="-1"` is set here rather than in the Liquid because the element is only ever a focus
   * target in this one transition; a permanently focusable container is a thing a keyboard user
   * tabs into for no reason on a page they arrived at with an already-empty cart.
   */
  #focusEmptyCart() {
    const target = this.querySelector('[ref="emptyCartContent"]') ?? this.firstElementChild;
    if (!(target instanceof HTMLElement)) return;
    target.setAttribute('tabindex', '-1');
    target.focus();
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    // Optional at every step: removing the LAST line swaps the list for the empty state before
    // this runs (it waits for the undo snapshot), so there are no rows left to read. Unguarded,
    // that threw here and the removal was never sent: the drawer showed an empty cart while the
    // server still held the line (measured on 208048619850 and 208733503818, 2026-10-01).
    const lineId = this.refs.cartItemRows?.[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    this.#cartChangeQueue = this.#cartChangeQueue
      .then(() => fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        const before = this.#caseState();
        const focusBack = this.#focusAnchor();
        const morphed = morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
        Promise.resolve(morphed).then(() => {
          this.#announceMilestone(before);
          this.#reapplyLineNotes();
          focusBack();
        });

        this.#updateCartQuantitySelectorButtonStates();

        // The queue waits for the morph, so anything queued after this change (an Undo) starts
        // from the cart on screen, not one this morph is about to replace.
        return Promise.resolve(morphed).then(() => this.#reofferUndoAfterMorph());
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        // The cart on screen goes back to the last saved state (a stepper that had moved on is
        // reset) and the banner offers the same change again.
        const input = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
        if (input instanceof HTMLInputElement) input.value = input.defaultValue;
        this.#showNetworkError(() => this.updateQuantity(config), input instanceof HTMLInputElement ? input : null);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);
          const before = this.#caseState();

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Just added (state A): the new line is tagged and tinted, and the drawer says so.
          if (this.isDrawer && event.action === 'add') {
            const added = (this.refs.cartItemRows ?? []).filter((row) => !existingKeys.has(row.dataset.key));
            const host = this.closest('dialog')?.querySelector('[data-cart-toasts]');
            const tag = host instanceof HTMLElement ? host.dataset.addedTag : '';
            for (const row of added) {
              row.classList.add('is-new');
              const link = row.querySelector('.cart-items__title');
              if (link && tag && !link.querySelector('.gv-line__new')) {
                const span = document.createElement('span');
                span.className = 'gv-line__new';
                span.textContent = tag;
                link.append(span);
              }
            }
            const first = added[0];
            const template = host instanceof HTMLElement ? host.dataset.added : '';
            if (first && template) this.#toast(template.replace('__TITLE__', first.dataset.title ?? ''));
            // Focus to the drawer's heading (state A), so a keyboard user starts at the top of the
            // cart they have just changed, rather than on <body> when the pressed control was
            // re-rendered away.
            const heading = this.closest('dialog')?.querySelector('#cart-drawer-heading');
            if (added.length && heading instanceof HTMLElement && this.closest('dialog')?.open) {
              heading.setAttribute('tabindex', '-1');
              heading.focus({ preventScroll: true });
            }
          }
          this.#announceMilestone(before);

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
